import { useEffect, useRef, useState } from 'react';
import { Menu, Building2, ChevronDown, Check, User } from 'lucide-react';
import { useCompany } from '../../contexts/CompanyContext';

export default function Header({ onMenuToggle }) {
    const { companies, homeCompany, activeCompanyId, switchCompany, isCompanyUser } = useCompany();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // The default entry (id: null - no X-Company-Id header sent) always
    // shows the user's own company name instead of a generic label.
    const homeOption = { id: null, name: homeCompany?.name || 'My Company' };
    const switcherOptions = [homeOption, ...companies];
    const selectedOption = switcherOptions.find((c) => c.id === activeCompanyId) || homeOption;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectCompany = (companyId) => {
        switchCompany(companyId);
        setIsOpen(false);
    };

    return (
        <header className="bg-white shadow p-4 flex justify-between items-center sticky top-0 z-30">
            <div className="flex items-center gap-3">
                {/* Hamburger menu for mobile */}
                <button
                    onClick={onMenuToggle}
                    className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    aria-label="Toggle menu"
                >
                    <Menu className="w-6 h-6 text-gray-600" />
                </button>
                <h1 className="text-lg sm:text-xl font-bold text-gray-700">Dentulu Developer Ecosystem</h1>
            </div>

            {isCompanyUser && (
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => setIsOpen((prev) => !prev)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                    {selectedOption.id === null
                        ? <User className="w-5 h-5 text-blue-600" />
                        : <Building2 className="w-5 h-5 text-blue-600" />}
                    <span className="hidden sm:inline text-sm font-medium text-gray-700 max-w-[10rem] truncate">
                        {selectedOption.name}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                    <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                        <p className="px-4 pb-1.5 text-xs font-semibold text-gray-400 tracking-wide uppercase">
                            Select Company
                        </p>
                        <ul className="max-h-64 overflow-y-auto">
                            {switcherOptions.map((option) => {
                                const isSelected = option.id === activeCompanyId;
                                const Icon = option.id === null ? User : Building2;
                                return (
                                    <li key={option.id ?? 'individual'}>
                                        <button
                                            onClick={() => handleSelectCompany(option.id)}
                                            className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors ${
                                                isSelected
                                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                                            <span className="truncate flex-1">{option.name}</span>
                                            {isSelected && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
            )}
        </header>
    );
}
